import { Ref, useCallback, useRef } from "react";
import { debounce } from "lodash";

interface IUseResizeWatcherOptions<
    TSource extends Element,
    TTarget extends Element = TSource,
> {
    resizeOnMount?: boolean;
    selector?: (el: TSource) => TTarget | null;
    wait?: number;
}

export function useResizeWatcher<
    TSource extends Element,
    TTarget extends Element = TSource,
>(
    onResize: (dimensions: DOMRectReadOnly, el: TSource) => void,
    options: IUseResizeWatcherOptions<TSource, TTarget> = {},
): { ref: Ref<TSource> } {
    const {
        wait,
        resizeOnMount = false,
        selector = (el: TSource): TTarget => el as unknown as TTarget,
    } = options;
    const unsub = useRef<(() => void) | null>(null);

    const ref = useCallback(
        (el: TSource | null): void => {
            unsub.current?.();
            unsub.current = null;

            if (el === null) {
                return;
            }

            const targetEl = selector(el);
            if (targetEl === null) {
                return;
            }

            const callbackFn = wait
                ? debounce(onResize, wait, { leading: false, trailing: true })
                : onResize;
            const observer = new ResizeObserver(entries => {
                const [entry] = entries;
                callbackFn(entry.contentRect, el);
            });
            observer.observe(targetEl);
            unsub.current = () => {
                if (
                    "cancel" in callbackFn &&
                    typeof callbackFn.cancel === "function"
                ) {
                    callbackFn.cancel();
                }
                observer.unobserve(targetEl);
                observer.disconnect();
            };

            if (resizeOnMount) {
                callbackFn(targetEl.getBoundingClientRect(), el);
            }
        },
        [onResize, wait, unsub, resizeOnMount, selector],
    );

    return { ref };
}
